import { useEffect, useRef, useState } from "react";
import AIapi from "../../../api/AIapi";
import "./collabAI.css";

const seedMessages = (collaborationName) => [
  {
    id: "seed",
    role: "ai",
    text: `Ask me anything about ${collaborationName || "this collaboration"} — clauses, dates, obligations, definitions. I'll answer from the collaboration itself.`,
  },
];
const CollabAI = ({ collabId, collaborationName }) => {
  const [messages, setMessages] = useState(() =>
    seedMessages(collaborationName),
  );
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isThinking]);

  const askQuestion = async ({ collabId: collabId, question }) => {
    if (!collabId) {
      return "I don't see a collaboration attached yet. Open a collaboration   first, then ask away.";
    }

    const payload = {
      sourceService: "COLLABORATION_SERVICE",
      entityType: "COLLABORATION",
      collaborationId: collabId,
      question: question,
    };

    const res = await AIapi.askQuestion(payload);

    return res.data;
  };

  const handleSend = async () => {
    const question = input.trim();
    if (!question || isThinking) return;

    const userMsg = { id: crypto.randomUUID(), role: "user", text: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setError(null);
    setIsThinking(true);

    try {
      const answer = await askQuestion({ collabId, question });
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "ai", text: String(answer) },
      ]);
    } catch (err) {
      setError(
        "Couldn't reach ConsentAI. Check your connection and try again.",
      );
    } finally {
      setIsThinking(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = Boolean(input.trim()) && !isThinking;

  return (
    <div className="consentai">
      {/* Header */}
      <div className="consentai-header">
        <Seal size={30} tone="paper" />
        <div className="consentai-title-group">
          <span className="consentai-title">ConsentAI</span>
          <span className="consentai-subtitle">
            {collaborationName
              ? `Reading: ${collaborationName}`
              : "Collaboration assistant"}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="consentai-messages">
        {messages.map((m) => (
          <Bubble key={m.id} role={m.role} text={m.text} />
        ))}

        {isThinking && (
          <div className="consentai-row consentai-row--ai">
            <Seal size={22} tone="moss" />
            <div className="consentai-typing">
              <span className="consentai-dot" />
              <span className="consentai-dot" />
              <span className="consentai-dot" />
            </div>
          </div>
        )}

        {error && (
          <div role="alert" className="consentai-error">
            {error}
          </div>
        )}
      </div>

      {/* Input — styled like a signature line */}
      <div className="consentai-inputbar">
        <div className="consentai-inputwrap">
          <textarea
            ref={inputRef}
            className="consentai-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about a clause, date, or obligation…"
            rows={1}
          />
          <button
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send question"
            className={`consentai-send ${canSend ? "consentai-send--active" : ""}`}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 8h11M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="consentai-hint">
          Enter to send · Shift+Enter for a new line
        </div>
      </div>
    </div>
  );
};

const Bubble = ({ role, text }) => {
  const isUser = role === "user";
  return (
    <div
      className={`consentai-row ${isUser ? "consentai-row--user" : "consentai-row--ai"}`}
    >
      {!isUser && <Seal size={22} tone="moss" />}
      <div
        className={`consentai-bubble ${
          isUser ? "consentai-bubble--user" : "consentai-bubble--ai"
        }`}
      >
        {text}
      </div>
    </div>
  );
};

const Seal = ({ size = 24, tone = "moss" }) => (
  <div
    className={`consentai-seal consentai-seal--${tone}`}
    style={{ width: size, height: size }}
  >
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 20 20" fill="none">
      <path d="M10 2l1.9 4.6L17 7l-4 3.5L14 16l-4-2.6L6 16l1-5.5L3 7l5.1-.4L10 2z" />
    </svg>
  </div>
);

export default CollabAI;
